import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Button } from '@/components/buttons/Button';
import { isEmpty } from '@/shared/utility';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthToken } from '@/store/selectors/authSelectors';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '../../../shared/bffApiService';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { getUser, getUserTags } from '@/store/actions/user';
import Form from '@/components/form/Form';
import PhoneNumberField from '@/components/form/phone-number/PhoneNumberField';

interface MexicoVerificationV3Form {
  phone: {
    country: string;
    number: string;
    prefix: string;
    wholeNumber: string;
  };
}

const initialValues: MexicoVerificationV3Form = {
  phone: { prefix: '', number: '', country: '', wholeNumber: '' },
};

const CompleteProfileMexicoPhone = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);
  const {
    request: postCompleteProfile,
    loading: completeProfileLoading,
    error: completeProfileError,
  } = useBffApiRequest();
  const [isSuccess, setIsSuccess] = useState(false);

  const validationSchema = Yup.object().shape({
    phone: Yup.object().shape({
      prefix: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string()
        .required(intl.formatMessage({ id: 'input.error.required' }))
        .min(10, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
  });

  const handleOnSubmit = ({ phone }: MexicoVerificationV3Form) => {
    postCompleteProfile({
      apiMethod: BffApiService.completeProfileMexicoV3,
      payload: {
        phone: { prefix: phone.prefix, number: phone.number },
      },
      successCallback: () => {
        dispatch(getUser(token));
        dispatch(getUserTags());
        setIsSuccess(true);
        triggerGTag(GTagEvents.complete_profile_success);
      },
    });
  };

  return (
    <div>
      {isSuccess ? (
        <div className="mx-auto max-w-[520px] md:p-8 bg-white md:rounded-xl md:drop-shadow-xl">
          <h2 className="text-green-500 text-[26px] md:text-3xl text-center m-0">
            {intl.formatMessage({ id: 'completeProfile.success.header' })}
          </h2>
          <p className="md:text-center mt-6 mx-0 mb-10">
            {intl.formatMessage({ id: 'completeProfile.success.text' })}
          </p>
          <Button
            type="button"
            text={intl.formatMessage({ id: 'misc.continue' })}
            onClickHandler={() => dispatch(getUserTags())}
          />
        </div>
      ) : (
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnBlur
          onSubmit={handleOnSubmit}
        >
          {({ values }) => (
            <div className="flex flex-col space-y-12 items-center w-full">
              <div className="flex flex-col space-y-6 w-full max-w-[520px] md:bg-white md:drop-shadow-xl md:px-8 md:py-12 rounded-xl">
                <div className="pb-6">
                  <h2 className="md:text-center m-0">
                    {intl.formatMessage({ id: 'completeProfile.prompt.header' })}
                  </h2>
                  <p className="md:text-center mt-3 m-0">
                    {intl.formatMessage({ id: 'completeProfile.prompt.text' })}
                  </p>
                </div>
                <PhoneNumberField
                  country="mx"
                  label={intl.formatMessage({ id: 'inputs.mobile' })}
                  className="border-[1px] border-gray-300"
                  labelClassName="text-blue-blue"
                  name="phone"
                />
                {completeProfileError && (
                  <p className="text-red-500 text-md">
                    {intl.formatMessage({ id: 'error.support' })}
                  </p>
                )}
                <div className="mx-auto w-full max-w-[200px] pt-6">
                  <Button
                    disabled={
                      !isEmpty(Object.values(values).filter(value => isEmpty(value))) ||
                      completeProfileLoading
                    }
                    type="submit"
                    text={intl.formatMessage({ id: 'misc.submit' })}
                  />
                </div>
              </div>
            </div>
          )}
        </Form>
      )}
    </div>
  );
};

export default CompleteProfileMexicoPhone;
